// apps/web/src/pages/qualidade/certificado-page.tsx — Fase 02, Plano 10 (QUAL-05, D-16).
import { useState, useEffect, type ReactElement } from 'react';
import { CorrecaoApontamentoForm, type ApontamentoResumo } from '../../components/qualidade/correcao-apontamento-form';
import type { CertificadoConsulta } from '@pieltec/contracts';

export function CertificadoPage(): ReactElement {
  const [loteId, setLoteId] = useState<string>('');
  const [certificado, setCertificado] = useState<CertificadoConsulta | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Form de Correção
  const [selectedApontamento, setSelectedApontamento] = useState<ApontamentoResumo | null>(null);

  const fetchCertificado = async (id: string) => {
    if (!id.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/qualidade/certificados/${encodeURIComponent(id)}`);
      if (!res.ok) {
        throw new Error('Certificado não encontrado para o lote informado.');
      }
      const data = (await res.json()) as CertificadoConsulta;
      setCertificado(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao consultar certificado');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qLote = params.get('loteId');
    if (qLote) {
      setLoteId(qLote);
      void fetchCertificado(qLote);
    }
  }, []);

  return (
    <div className="certificado-page min-h-screen bg-slate-100 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header de Busca */}
        <header className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Certificados de qualidade</h1>
            <p className="text-xs text-slate-500 mt-1">
              Consulte o certificado do lote e confira os requisitos para liberação.
            </p>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              aria-label="Identificação do lote" placeholder="Código ou identificação do lote"
              value={loteId}
              onChange={(e) => setLoteId(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono w-full sm:w-64 focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => void fetchCertificado(loteId)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-sm"
            >
              Consultar
            </button>
          </div>
        </header>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl font-medium" role="alert">
            {error}
          </div>
        )}

        {loading && (
          <div className="py-12 text-center text-xs text-slate-500 bg-white rounded-2xl border border-slate-200">
            Consultando registros de qualidade do lote...
          </div>
        )}

        {certificado && !loading && (
          <main className="space-y-6 animate-fade-in">
            {/* Banner de Status D-16 (LIBERADO / BLOQUEADO) */}
            <div
              className={`p-6 rounded-2xl border shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                certificado.statusCertificado === 'LIBERADO'
                  ? 'bg-emerald-50/80 border-emerald-200 text-emerald-900'
                  : 'bg-purple-50/80 border-purple-200 text-purple-900'
              }`}
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                      certificado.statusCertificado === 'LIBERADO'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-purple-700 text-white'
                    }`}
                  >
                    {certificado.statusCertificado}
                  </span>
                  <h2 className="text-xl font-bold">
                    Lote {certificado.loteCodigo} — {certificado.itemId}
                  </h2>
                </div>
                <p className="text-xs">
                  {certificado.statusCertificado === 'LIBERADO'
                    ? 'Todos os gates técnicos foram atendidos com sucesso. Certificado liberado para expedição.'
                    : 'A liberação deste lote está bloqueada devido a pendências nos gates de qualidade.'}
                </p>
              </div>

              {/* Lista dos 4 Gates D-16 */}
              <div className="grid grid-cols-2 gap-2 text-xs font-mono bg-white/70 p-3 rounded-xl border border-black/5">
                {certificado.gates.map((g) => (
                  <div key={g.nome} className="flex items-center gap-1.5" title={g.motivo}>
                    <span className={g.atendido ? 'text-emerald-600 font-bold' : 'text-red-600 font-bold'}>
                      {g.atendido ? '✓' : '✕'}
                    </span>
                    <span className={g.atendido ? 'text-slate-700' : 'text-red-700 font-semibold'}>
                      {g.nome}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Seção 1: Dados do Lote & OP */}
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Identificação Geral</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-mono">
                <div>
                  <span className="text-slate-400 block text-[10px]">LOTE INTERNO:</span>
                  <span className="font-bold text-slate-800">{certificado.loteCodigo}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">ITEM / MATERIAL:</span>
                  <span className="font-bold text-slate-800">{certificado.itemId}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">ORDEM DE PRODUÇÃO:</span>
                  <span className="font-bold text-slate-800">
                    {certificado.op ? `OP-${certificado.op.numero} (${certificado.op.status})` : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">REMESSA ID:</span>
                  <span className="text-slate-600">{certificado.remessaId ?? 'Sem vínculo'}</span>
                </div>
              </div>
            </section>

            {/* Seção 2: Apontamentos e Ações de Correção */}
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                  Trilha de Apontamentos ({certificado.apontamentos.length})
                </h3>
              </div>

              {certificado.apontamentos.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">Nenhum apontamento registrado para o lote.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {certificado.apontamentos.map((apt) => (
                    <div key={apt.id} data-apontamento-corrigido={apt.corrigido ? 'true' : 'false'} className="py-3 flex items-center justify-between gap-4 text-xs">
                      <div className="space-y-0.5 font-mono">
                        <div className="text-slate-800 font-semibold">
                          Qtd Boa: <span className="font-bold">{apt.quantidadeBoa} un</span> · NC:{' '}
                          <span className={apt.quantidadeNaoConforme > 0 ? 'text-red-600 font-bold' : 'text-slate-600'}>
                            {apt.quantidadeNaoConforme} un
                          </span>
                        </div>
                        {apt.motivoNc && <div className="text-slate-500 text-[11px]">Motivo NC: {apt.motivoNc}</div>}
                        <div className="text-[10px] text-slate-400">
                          ID: {apt.id.slice(0, 8)}... · Data: {new Date(apt.createdAt).toLocaleString()}
                        </div>
                        {apt.corrigido && <div className="text-[11px] font-semibold text-amber-700">Corrigido — substituído por {apt.substituidoPorId?.slice(0, 8) ?? 'registro posterior'}</div>}
                        {apt.correcaoDeId && <div className="text-[11px] font-semibold text-emerald-700">Correção de {apt.correcaoDeId.slice(0, 8)}</div>}
                      </div>

                      {!apt.corrigido && <button
                        type="button"
                        onClick={() =>
                          setSelectedApontamento({
                            id: apt.id,
                            osId: apt.osId,
                            quantidadeBoa: apt.quantidadeBoa,
                            quantidadeNaoConforme: apt.quantidadeNaoConforme,
                            motivoNc: apt.motivoNc,
                            createdAt: apt.createdAt,
                          })
                        }
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg border border-slate-300 transition"
                      >
                        Corrigir Apontamento
                      </button>}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Seção 3: Não Conformidades */}
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                Não Conformidades Registradas ({certificado.naoConformidades.length})
              </h3>
              {certificado.naoConformidades.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">Zero não conformidades neste lote.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {certificado.naoConformidades.map((nc) => (
                    <div key={nc.id} className="py-3 flex items-center justify-between text-xs font-mono">
                      <div>
                        <span className="font-bold text-slate-800">{nc.motivo}</span>
                        <span className="text-slate-500 block text-[11px]">{nc.quantidade} un afetadas</span>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          nc.status === 'DESTINADA' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {nc.status} {nc.destinacao && `(${nc.destinacao})`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Seção 4: Especificações & Inspeções Técnicas */}
            <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                Inspeções e Especificações Técnicas ({certificado.inspecoes.length})
              </h3>
              {certificado.inspecoes.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">
                  Nenhuma inspeção de qualidade realizada para este lote.
                </div>
              ) : (
                <table className="w-full text-left text-xs font-mono">
                  <thead className="bg-slate-50 text-slate-600 uppercase border-b border-slate-200">
                    <tr>
                      <th className="p-3">Especificação</th>
                      <th className="p-3">Faixa Tolerada</th>
                      <th className="p-3">Valor Medido</th>
                      <th className="p-3 text-right">Resultado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {certificado.inspecoes.map((insp) => (
                      <tr key={insp.id}>
                        <td className="p-3 font-bold text-slate-800">{insp.especificacao}</td>
                        <td className="p-3 text-slate-600">
                          {insp.toleranciaMin} a {insp.toleranciaMax}
                        </td>
                        <td className="p-3 font-bold text-slate-900">{insp.valorMedido}</td>
                        <td className="p-3 text-right">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              insp.resultado === 'DENTRO'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {insp.resultado}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </main>
        )}
      </div>

      {/* Modal de Correção de Apontamento */}
      {selectedApontamento && (
        <CorrecaoApontamentoForm
          apontamentoOriginal={selectedApontamento}
          open={Boolean(selectedApontamento)}
          onClose={() => setSelectedApontamento(null)}
          onCorrigido={() => {
            if (loteId) void fetchCertificado(loteId);
          }}
        />
      )}
    </div>
  );
}
